// ahri_v5 — domain/availability.js

import { WAREHOUSE_CODE_STORAGE_KEY, SHIP_TO_ZIP_STORAGE_KEY, scopeStorageKey } from "../constants/widget.js";
import { partPriceLookupKeys } from "./pricing.js";
import { isTruthyLike } from "./ahriRecords.js";

export function normalizeWarehouseCode(value) {
  return String(value || "").trim().toUpperCase();
}

export function normalizeWarehouseDetails(value) {
  if (!Array.isArray(value)) return [];
  return value
    .map((entry) => {
      if (!entry || typeof entry !== "object") return null;
      const branchCode = normalizeWarehouseCode(entry.branchCode || entry.warehouseCode || entry.code);
      if (!branchCode) return null;
      return {
        ...entry,
        branchCode,
        branchName: String(entry.branchName || entry.name || branchCode).trim(),
        locality: String(entry.locality || "").trim(),
        street: String(entry.street || "").trim(),
      };
    })
    .filter(Boolean);
}

export function findWarehouseDetailByCode(warehouseDetails, code) {
  const normalizedCode = normalizeWarehouseCode(code);
  if (!normalizedCode) return null;
  return warehouseDetails.find((entry) => normalizeWarehouseCode(entry.branchCode) === normalizedCode) || null;
}

export function formatWarehouseOptionLabel(warehouse) {
  if (!warehouse) return "";
  return [warehouse.branchName, warehouse.locality, warehouse.street].filter(Boolean).join(" - ");
}

export function resolveWarehouseDisplayLabel(warehouseCode, warehouseOptions = []) {
  const code = normalizeWarehouseCode(warehouseCode);
  if (!code) return "";
  const detail = findWarehouseDetailByCode(warehouseOptions, code);
  if (!detail) return code;
  return formatWarehouseOptionLabel(detail) || detail.branchName || code;
}

export function normalizeShipToZip(value) {
  const digits = String(value || "").replace(/\D+/g, "");
  if (!digits) return "";
  return digits.slice(0, 5);
}
export function resolveWarehouseCodeProp(
  branchCode = "",
  defaultBranchCode = "",
  warehouseCode = "",
  branch_code = "",
  default_branch_code = "",
  warehouse_code = ""
) {
  return normalizeWarehouseCode(
    branchCode
    || defaultBranchCode
    || warehouseCode
    || branch_code
    || default_branch_code
    || warehouse_code
  );
}

export function loadInitialWarehouseCode(initialValue = "", warehouseDetails = [], accountId = "") {
  const fromProp = normalizeWarehouseCode(initialValue);
  if (fromProp) return fromProp;
  const firstWarehouseCode = normalizeWarehouseCode(warehouseDetails?.[0]?.branchCode);
  if (firstWarehouseCode) return firstWarehouseCode;
  if (typeof window === "undefined") return "";
  try {
    return normalizeWarehouseCode(window.localStorage.getItem(scopeStorageKey(WAREHOUSE_CODE_STORAGE_KEY, accountId)) || "");
  } catch {
    return "";
  }
}

export function resolveShipToZipProp(shipToZip = "", ship_to_zip = "") {
  return normalizeShipToZip(shipToZip || ship_to_zip);
}

export function loadInitialShipToZip(initialValue = "", accountId = "") {
  const fromProp = normalizeShipToZip(initialValue);
  if (fromProp) return fromProp;
  if (typeof window === "undefined") return "";
  try {
    const params = new URLSearchParams(window.location.search || "");
    const fromQuery = normalizeShipToZip(
      params.get("shipToZip")
      || params.get("ship_to_zip")
      || params.get("zip")
      || ""
    );
    if (fromQuery) return fromQuery;
    return normalizeShipToZip(window.localStorage.getItem(scopeStorageKey(SHIP_TO_ZIP_STORAGE_KEY, accountId)) || "");
  } catch {
    return "";
  }
}
export function getProductAvailability(detail, selectedWarehouse) {
  const normalizedWarehouse = normalizeWarehouseCode(selectedWarehouse);
  const availability = detail?.availability && typeof detail.availability === "object" ? detail.availability : {};
  const inSelectedWarehouse = isTruthyLike(availability.in_selected_warehouse);
  const detailWarehouse = normalizeWarehouseCode(availability.selected_warehouse);
  const otherWarehouseCodes = Array.isArray(availability.other_warehouse_codes)
    ? availability.other_warehouse_codes.map((entry) => normalizeWarehouseCode(entry)).filter(Boolean)
    : [];
  const hasWarehouseData = !normalizedWarehouse || (detailWarehouse && detailWarehouse === normalizedWarehouse);
  return {
    inSelectedWarehouse,
    availableInOtherWarehouses: !!availability.available_in_other_warehouses || otherWarehouseCodes.length > 0,
    otherWarehouseCodes,
    selectedWarehouse: normalizedWarehouse,
    hasWarehouseData,
  };
}

/** Case-insensitive lookup in the widget's product-details cache. */
export function lookupProductDetail(productCode, productDetails) {
  if (!productDetails || typeof productDetails !== "object") return {};
  const keys = partPriceLookupKeys(productCode);
  for (let i = 0; i < keys.length; i += 1) {
    const detail = productDetails[keys[i]];
    if (detail && typeof detail === "object") return detail;
  }
  const upper = String(productCode || "").trim().toUpperCase();
  const fromUpper = upper ? productDetails[upper] : null;
  return fromUpper && typeof fromUpper === "object" ? fromUpper : {};
}

/**
 * Whether a product is in stock at the selected warehouse.
 * Unknown/pending availability returns false unless `treatUnknownAsInStock` is set.
 */
export function isProductInStockAtWarehouse(
  detail,
  selectedWarehouse,
  { treatUnknownAsInStock = false } = {}
) {
  const normalizedWarehouse = normalizeWarehouseCode(selectedWarehouse);
  if (!normalizedWarehouse) return true;
  const availability = getProductAvailability(detail, normalizedWarehouse);
  if (!availability.hasWarehouseData) return treatUnknownAsInStock;
  return availability.inSelectedWarehouse;
}

/**
 * Map product availability into the green/red/grey dot class used on cards.
 * `pending` (the detail lookup for this code is in flight) yields "loading"
 * instead of "unknown" while the selected warehouse's data isn't in yet.
 */
export function getAvailabilityDotClass(detail, selectedWarehouse, { pending = false } = {}) {
  const normalizedWarehouse = normalizeWarehouseCode(selectedWarehouse);
  if (!normalizedWarehouse) return "unknown";
  const availability = getProductAvailability(detail, normalizedWarehouse);
  if (!availability.hasWarehouseData) return pending ? "loading" : "unknown";
  return availability.inSelectedWarehouse ? "available" : "unavailable";
}

/**
 * Pick the product code shown for one AHRI slot on a system card.
 * Drops a stale shopper selection when it is confirmed out of stock and an
 * in-stock alternative exists; otherwise keeps the choice while stock is loading.
 */
export function resolveDisplayedSlotCode(
  componentSelections,
  systemId,
  slotType,
  fallback,
  componentOptions,
  productDetails,
  selectedWarehouse
) {
  const codes = componentOptions?.[slotType]?.codes || [];
  const chosen = String(componentSelections?.[`${systemId}:${slotType}`] || "").trim();
  if (chosen && codes.some((code) => String(code || "").trim() === chosen)) {
    if (!normalizeWarehouseCode(selectedWarehouse)) return chosen;
    const chosenDetail = lookupProductDetail(chosen, productDetails);
    const chosenAvailability = getProductAvailability(chosenDetail, selectedWarehouse);
    const hasConfirmedInStockAlternative = codes.some((candidate) =>
      isProductInStockAtWarehouse(lookupProductDetail(candidate, productDetails), selectedWarehouse)
    );
    if (
      !chosenAvailability.hasWarehouseData
      || chosenAvailability.inSelectedWarehouse
      || !hasConfirmedInStockAlternative
    ) {
      return chosen;
    }
  }
  const normalizedWarehouse = normalizeWarehouseCode(selectedWarehouse);
  if (normalizedWarehouse) {
    const inStock = codes.find((candidate) =>
      isProductInStockAtWarehouse(lookupProductDetail(candidate, productDetails), selectedWarehouse)
    );
    if (inStock) return String(inStock).trim();
  }
  return String(fallback || codes[0] || "").trim();
}

/**
 * Decide which option codes to show for one AHRI system slot (Outdoor/Indoor/Furnace).
 *
 * Rules:
 * - An active product filter collapses the slot to the filtered code(s).
 * - With a branch selected, only options in stock at that branch are shown so the
 *   card honors the "complete match / fully available at this branch" settings and
 *   hides alternatives that aren't available there.
 * - If the slot has no in-stock option at the branch — e.g. the system was surfaced
 *   from other locations because nothing matched at the branch — the full option
 *   list is returned so the shopper can still see and shop the system. Once they
 *   switch to a branch where it is available (the "shop other location" flow), this
 *   falls back into the regular in-stock-only behavior.
 * - With no branch selected, every option is shown.
 *
 * @param {string[]} allCodes Every approved option code for the slot.
 * @param {object} options
 * @param {string} [options.selectedWarehouse] Currently selected branch/warehouse code.
 * @param {(code: string) => boolean} [options.isInStock] Predicate: is the code in stock at the branch.
 * @param {string[]} [options.filteredCodes] Active product-filter codes for this slot.
 * @returns {string[]} The option codes to render for the slot.
 */
export function resolveVisibleSlotCodes(
  allCodes,
  { selectedWarehouse = "", isInStock = () => false, filteredCodes = [] } = {}
) {
  const codes = (Array.isArray(allCodes) ? allCodes : [])
    .map((code) => String(code || "").trim())
    .filter(Boolean);
  const filtered = (Array.isArray(filteredCodes) ? filteredCodes : [])
    .map((code) => String(code || "").trim())
    .filter(Boolean);
  if (filtered.length) return filtered;
  if (!normalizeWarehouseCode(selectedWarehouse)) return codes;
  const inStock = codes.filter((code) => isInStock(code));
  return inStock.length ? inStock : codes;
}

/** Collect every approved component product code from Solr result docs. */
export function collectProductCodesFromDocs(docs) {
  const codes = new Set();
  (Array.isArray(docs) ? docs : []).forEach((doc) => {
    if (!doc || typeof doc !== "object") return;
    const options = parseComponentOptions(doc);
    Object.values(options).forEach((slot) => {
      (slot?.codes || []).forEach((code) => {
        const normalized = String(code || "").trim();
        if (normalized) codes.add(normalized);
      });
    });
  });
  return [...codes];
}

/** True when the shopper typed an AHRI ID or part/model number rather than a broad browse query. */
export function isDirectLookupQuery(query) {
  const text = String(query || "").trim();
  if (!text || text === "*:*") return false;
  if (/^\d{5,}$/.test(text)) return true;
  if (text.length >= 4 && !/\s/.test(text) && /^[A-Za-z0-9._/*+-]+$/.test(text)) {
    if (/[A-Za-z]/.test(text) && /\d/.test(text)) return true;
  }
  if (/^[A-Za-z0-9-]{4,}$/.test(text) && !/\s/.test(text)) return true;
  return false;
}

/** Map a slot availability dot class into a shopper-facing status pill ({ tone, text }). */
export function getSlotAvailabilityStatus(dotClass, warehouseCode, warehouseLabel = "") {
  const wh = normalizeWarehouseCode(warehouseCode);
  if (!wh || dotClass === "unknown") return null;
  const label = String(warehouseLabel || "").trim() || wh;
  if (dotClass === "available") return { tone: "available", text: `In stock at ${label}` };
  if (dotClass === "unavailable") return { tone: "unavailable", text: `Not at ${label}` };
  return null;
}
